"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.openAgentStatusViewSchema = exports.openAgentListViewSchema = exports.openAgentListItemSchema = exports.OPEN_AGENT_VIEW_KEYS = exports.openAgentViewSchema = exports.openAgentStatusSchema = exports.OPEN_AGENT_UPDATE_FORBIDDEN_KEYS = exports.openAgentUpdateSchema = exports.openAgentCreateSchema = exports.openAgentListQuerySchema = exports.openAgentTenantSchema = void 0;
const zod_1 = require("zod");
const customer_service_1 = require("./customer-service");
/**
 * 开放管理 API（/open/v1/agents）蛇形契约 —— 服务级 API 的请求/响应单一事实源。
 *
 * 设计文档 docs/dev/open-api-management-design.md（v0.2 服务级模型）：
 *  - 鉴权 header X-Api-Key（静态服务 key，不绑定商户）；
 *  - 商户身份由请求参数显式携带：merchant_id 必传，site_id 可选（缺省取 BE 环境 SITE_DEFAULT_ID）；
 *  - 字段一律蛇形；与 admin 域驼峰 schema 的映射在 BE OpenAgentsService 完成。
 *
 * ⚠️ 变更纪律（CLAUDE.md 同步条款）：CustomerService 实体 / admin 客服 schema 变更时，
 *   必须同步评估本文件是否变更（开放 or 不开放新字段），BE 契约测试会快照响应字段集合。
 */
/** 租户参数（每个端点必带）：merchant_id 必填，site_id 可选。 */
exports.openAgentTenantSchema = zod_1.z.object({
    merchant_id: zod_1.z.string().uuid('merchant_id 必须为合法 UUID'),
    site_id: zod_1.z.string().uuid('site_id 必须为合法 UUID').optional(),
});
/** 列表 query：租户参数 + 分页 + 过滤。 */
exports.openAgentListQuerySchema = exports.openAgentTenantSchema.extend({
    page: zod_1.z.coerce.number().int().min(1).default(1),
    page_size: zod_1.z.coerce.number().int().min(1).max(100).default(20),
    type: customer_service_1.customerServiceTypeEnum.optional(),
    status: zod_1.z.enum(['active', 'inactive']).optional(),
    keyword: zod_1.z.string().max(64).optional(),
});
/**
 * 业务字段（创建/更新共用基座）。蛇形版 customerServiceWriteSchema：
 *  - avatar_key/wechat_qr_key 不开放（内部 OBS key），改为 avatar_url/wechat_qr_url
 *    公网图 URL，BE 下载转存私有桶（asset-ingest，失败 1424）；
 *  - system_avatar_url 不开放（编辑期临时预览值，BE 归一置空）。
 */
const openAgentWriteSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(100),
    system_avatar_id: zod_1.z.string().max(64).nullable().optional(),
    avatar_url: zod_1.z.string().url().max(1024).nullable().optional(),
    theme_color: zod_1.z.string().max(16).nullable().optional(),
    title_bg_gradient: zod_1.z
        .string()
        .max(128)
        .regex(customer_service_1.TITLE_BG_GRADIENT_RE, '渐变格式必须为 linear-gradient(<角度>deg, #RRGGBB, #RRGGBB)')
        .nullable()
        .optional(),
    welcome_msg: zod_1.z.string().max(1000).nullable().optional(),
    guide_msg: zod_1.z.string().max(1000).nullable().optional(),
    fallback_msg: zod_1.z.string().max(1000).nullable().optional(),
    human_welcome_msg: zod_1.z.string().max(1000).nullable().optional(),
    kb_ids: zod_1.z.array(zod_1.z.string().min(1).max(64)).max(50).optional(),
    voice_id: zod_1.z.string().max(64).nullable().optional(),
    show_button: zod_1.z.boolean().optional(),
    default_window: customer_service_1.defaultWindowEnum.optional(),
    button_position: customer_service_1.buttonPositionEnum.optional(),
    offset_unit: customer_service_1.offsetUnitEnum.optional(),
    offset_x: zod_1.z.number().int().min(0).max(customer_service_1.OFFSET_MAX_PX).optional(),
    offset_y: zod_1.z.number().int().min(0).max(customer_service_1.OFFSET_MAX_PX).optional(),
    invoke_event_name: zod_1.z
        .string()
        .min(1)
        .max(64)
        .regex(customer_service_1.INVOKE_EVENT_NAME_RE, '事件名须字母开头,仅含字母数字及 : _ - ,长度≤64')
        .optional(),
    phone: zod_1.z.string().max(32).nullable().optional(),
    wechat_qr_url: zod_1.z.string().url().max(1024).nullable().optional(),
    work_time: zod_1.z.string().max(128).nullable().optional(),
    i18n: customer_service_1.customerServiceI18nSchema,
    site_ids: zod_1.z.array(zod_1.z.string().uuid()).max(200).optional(),
});
/**
 * 跨字段校验（create/update 共用同一函数，字段 undefined 时跳过）：
 *  ① percent 单位下偏移上限收紧为 90（对齐 admin 域 refineOffsetPercent）；
 *  ② system_avatar_id 与 avatar_url 互斥（同传视为参数错误）；
 *  ③ type=web 时不允许 voice_id / system_avatar_id（仅数字人客服可配置）。
 */
const refineOpenAgentWrite = (val, ctx) => {
    if (val.offset_unit === 'percent') {
        for (const key of ['offset_x', 'offset_y']) {
            const v = val[key];
            if (typeof v === 'number' && v > customer_service_1.OFFSET_MAX_PERCENT) {
                ctx.addIssue({
                    code: 'custom',
                    path: [key],
                    message: `percent 模式下偏移不能超过 ${customer_service_1.OFFSET_MAX_PERCENT}`,
                });
            }
        }
    }
    if (val.system_avatar_id && val.avatar_url) {
        ctx.addIssue({
            code: 'custom',
            path: ['avatar_url'],
            message: 'system_avatar_id 与 avatar_url 互斥，只能传一个',
        });
    }
    if (val.type === 'web') {
        if (val.voice_id) {
            ctx.addIssue({
                code: 'custom',
                path: ['voice_id'],
                message: 'voice_id 仅数字人客服可配置',
            });
        }
        if (val.system_avatar_id) {
            ctx.addIssue({
                code: 'custom',
                path: ['system_avatar_id'],
                message: 'system_avatar_id 仅数字人客服可配置',
            });
        }
    }
};
/**
 * 创建入参：type/name 必填 + 租户参数（merchant_id 必填，site_id 可选）。
 * type 创建后不可修改；status 恒为 active 由 BE 置。
 */
exports.openAgentCreateSchema = exports.openAgentTenantSchema
    .extend({
    type: customer_service_1.customerServiceTypeEnum,
})
    .merge(openAgentWriteSchema)
    .superRefine(refineOpenAgentWrite);
/**
 * 更新入参：业务字段全 partial（传什么改什么）。
 * 不允许 type / merchant_id / site_id（租户不可迁移、类型不可变）——
 * zod 默认 strip 未声明字段,为显式拒绝这三个字段用 strict 语义单独校验:
 * BE controller 在解析前先检查 body 是否含这三个键,含则 400。
 * 注：superRefine 后类型为 ZodEffects，无法再 .partial()，故先 partial 再 superRefine。
 * 更新时 type 未知,规则③（web 禁 voice/system_avatar）由 BE 拿到实体后兜底校验。
 */
exports.openAgentUpdateSchema = openAgentWriteSchema
    .partial()
    .superRefine(refineOpenAgentWrite);
/** 更新 body 中显式禁止出现的键（BE controller 预检,出现即 400）。 */
exports.OPEN_AGENT_UPDATE_FORBIDDEN_KEYS = [
    'type',
    'merchant_id',
    'site_id',
];
/** 启停入参。 */
exports.openAgentStatusSchema = zod_1.z.object({
    status: zod_1.z.enum(['active', 'inactive']),
});
/**
 * 详情/创建/更新出参（完整字段,蛇形）。
 * 相对 admin 域 customerServiceViewSchema 的差异：
 *  - 不暴露 avatar_key/wechat_qr_key/system_avatar_url/kb_names（内部实现细节）；
 *  - merchant_id/site_id 回显（服务级 API,调用方需确认操作落点）。
 */
exports.openAgentViewSchema = zod_1.z.object({
    agent_id: zod_1.z.string().uuid(),
    client_id: zod_1.z.string().uuid(),
    merchant_id: zod_1.z.string().uuid(),
    site_id: zod_1.z.string().uuid(),
    type: customer_service_1.customerServiceTypeEnum,
    name: zod_1.z.string(),
    status: zod_1.z.string(),
    avatar_source: zod_1.z.enum(['system', 'custom']).nullable(),
    system_avatar_id: zod_1.z.string().nullable(),
    avatar_url: zod_1.z.string().nullable(),
    theme_color: zod_1.z.string().nullable(),
    title_bg_gradient: zod_1.z.string().nullable(),
    welcome_msg: zod_1.z.string().nullable(),
    guide_msg: zod_1.z.string().nullable(),
    fallback_msg: zod_1.z.string().nullable(),
    human_welcome_msg: zod_1.z.string().nullable(),
    voice_id: zod_1.z.string().nullable(),
    avatar_video_status: zod_1.z.enum(['none', 'pending', 'generating', 'ready', 'failed']),
    avatar_video_url: zod_1.z.string().nullable(),
    avatar_video_duration_ms: zod_1.z.number().int().nullable(),
    kb_ids: zod_1.z.array(zod_1.z.string()),
    show_button: zod_1.z.boolean(),
    default_window: customer_service_1.defaultWindowEnum,
    button_position: customer_service_1.buttonPositionEnum,
    offset_unit: customer_service_1.offsetUnitEnum,
    offset_x: zod_1.z.number().int(),
    offset_y: zod_1.z.number().int(),
    invoke_event_name: zod_1.z.string(),
    phone: zod_1.z.string().nullable(),
    wechat_qr_url: zod_1.z.string().nullable(),
    work_time: zod_1.z.string().nullable(),
    site_ids: zod_1.z.array(zod_1.z.string()),
    i18n: customer_service_1.customerServiceI18nSchema,
    created_at: zod_1.z.string(),
    updated_at: zod_1.z.string(),
});
/** 响应字段集合快照（契约测试断言用；新增字段必须显式加在这里）。 */
exports.OPEN_AGENT_VIEW_KEYS = Object.keys(exports.openAgentViewSchema.shape).sort();
/** 列表项（详情字段精简集）。 */
exports.openAgentListItemSchema = exports.openAgentViewSchema.pick({
    agent_id: true,
    client_id: true,
    merchant_id: true,
    type: true,
    name: true,
    status: true,
    avatar_source: true,
    avatar_url: true,
    avatar_video_status: true,
    created_at: true,
    updated_at: true,
});
/** 列表出参。 */
exports.openAgentListViewSchema = zod_1.z.object({
    items: zod_1.z.array(exports.openAgentListItemSchema),
    total: zod_1.z.number().int(),
    page: zod_1.z.number().int(),
    page_size: zod_1.z.number().int(),
});
/** 启停出参。 */
exports.openAgentStatusViewSchema = zod_1.z.object({
    agent_id: zod_1.z.string().uuid(),
    status: zod_1.z.string(),
});
//# sourceMappingURL=open-agent.js.map